import { CircleCheck, Gift, Info, TriangleAlert, Users, X, OctagonAlert } from 'lucide-preact';
import { MODE_INFO } from '@vm/content';
import { audio } from '../../game/audio/Audio';
import { dismissModal, modals, route } from '../state/nav';
import { cancelQueue } from '../state/play';
import { dismissToast, queue, toasts, type ToastKind } from '../state/store';
import { fmtClock } from './format';
import { useNow } from './hooks';

const TOAST_ICON: Record<ToastKind, preact.ComponentChildren> = {
  info: <Info size={22} />,
  success: <CircleCheck size={22} />,
  error: <OctagonAlert size={22} />,
  warning: <TriangleAlert size={22} />,
  reward: <Gift size={22} />,
  social: <Users size={22} />,
};

export function Toasts() {
  const list = toasts.value;
  if (!list.length) return null;
  return (
    <div class="toasts" role="status" aria-live="polite">
      {list.map((t) => (
        <div key={t.id} class={`toast k-${t.kind}`}>
          <span class="toast-icon">{TOAST_ICON[t.kind]}</span>
          <div class="toast-text">
            <b>{t.title}</b>
            {t.body && <span>{t.body}</span>}
            {t.actions && (
              <div class="toast-actions">
                {t.actions.map((a) => (
                  <button
                    key={a.label}
                    class={`btn sm ${a.primary ? 'primary' : ''}`}
                    onClick={() => {
                      audio.play('ui_click');
                      dismissToast(t.id);
                      void a.onClick();
                    }}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button class="toast-x" aria-label="Dismiss" onClick={() => dismissToast(t.id)}>
            <X size={16} strokeWidth={3} />
          </button>
        </div>
      ))}
    </div>
  );
}

export function ModalLayer() {
  const list = modals.value;
  if (!list.length) return null;
  return (
    <>
      {list.map((m, i) => (
        <div key={m.id} class={`modal-layer ${i === list.length - 1 ? 'is-top' : ''}`} onClick={() => dismissModal(m.id)}>
          {m.render(() => {
            modals.value = modals.value.filter((x) => x.id !== m.id);
          })}
        </div>
      ))}
    </>
  );
}

/** Matchmaking status visible on every screen except the lobby (which has its own). */
export function QueuePill() {
  const q = queue.value;
  const now = useNow(500);
  if (q.state !== 'searching' || route.value.screen === 'lobby') return null;
  return (
    <div class="queue-pill">
      <span class="queue-pulse" />
      <span>
        Finding <b>{MODE_INFO[q.mode]?.name ?? q.mode}</b> · {fmtClock((now - q.since) / 1000)}
      </span>
      <button class="queue-pill-x" aria-label="Cancel matchmaking" onClick={() => cancelQueue()}>
        <X size={18} strokeWidth={3} />
      </button>
    </div>
  );
}
